/**
 * Materialize rig bones the scene doesn't have — the render-side counterpart
 * of tip synthesis (ADR-010 §12): synthesized bones are REAL bones in the
 * RigDescriptor, so a presented scene must grow the matching nodes or the
 * retargeted clip's tracks would bind to nothing (first light found exactly
 * this: Head_HeadTop_End_synth had no scene node). Creation is loud — every
 * added node is returned; a bone whose parent is missing from the scene is a
 * rig/scene mismatch and throws.
 * Depends on: three, @harryhausen/kernel (types). Depended on by: app.
 */
import { Bone, type Object3D } from 'three';
import type { RigDescriptor } from '@harryhausen/kernel';

export function materializeMissingBones(root: Object3D, bones: RigDescriptor['bones']): string[] {
  const created: string[] = [];
  for (const bone of bones) {
    if (root.getObjectByName(bone.name) !== undefined) continue;
    if (bone.parent === null) {
      throw new Error(`rig root "${bone.name}" is absent from the scene — rig and scene do not match`);
    }
    const parentNode = root.getObjectByName(bone.parent);
    if (parentNode === undefined) {
      throw new Error(
        `cannot materialize "${bone.name}": its parent "${bone.parent}" is absent from the scene`,
      );
    }
    const node = new Bone();
    node.name = bone.name;
    const { position: p, rotation: q, scale: s } = bone.localBind;
    node.position.set(p.x, p.y, p.z);
    node.quaternion.set(q.x, q.y, q.z, q.w);
    node.scale.set(s.x, s.y, s.z);
    parentNode.add(node);
    created.push(bone.name);
  }
  return created;
}
